import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

export const SETUP_RULE_TEXT: Record<CmChecklist.SetupRuleId, { field: string; rule: string }> = {
  'setup-account-name': {
    field: 'Account name',
    rule: 'Account name should match the hotel name on their website.',
  },
  'setup-account-abn': { field: 'ABN', rule: 'Australia: ABN Verified must be ticked.' },
  'setup-account-currency': {
    field: 'Hotel account currency',
    rule: 'Hotel account currency should match the opportunity cost price currency.',
  },
  'setup-campaign-schedules-exclusions': {
    field: 'Campaign Schedules exclude selected brand / market',
    rule: 'Campaign schedules should not include selected brand / market exclusions.',
  },
  'setup-marketing-terms': {
    field: 'Marketing terms',
    rule: 'Auto-approve if marketing terms are consistent with brand.',
  },
  'setup-contracts-geo': { field: 'Geo', rule: 'Auto-approve if an active contract template exists for this geo.' },
  'setup-sell-cost-reporting-currency': {
    field: 'Cost / reporting currency',
    rule: 'Auto-approve if cost and reporting currency are the same.',
  },
};

// `abn_required` has no label: it only drives the ABN summary and is not shown as a data point.
export const SETUP_EVIDENCE_LABELS: Partial<Record<CmChecklist.SetupEvidenceKey, string>> = {
  account_name: 'Account name',
  property_name: 'Property name',
  property_country: 'Property country',
  abn_verified: 'ABN verified',
  hotel_account_currency: 'Hotel account currency',
  opportunity_currency: 'Opportunity cost price currency',
  excluded_brands: 'Excluded brands',
  excluded_countries: 'Excluded countries',
  loaded_schedule_rows: 'Loaded schedule rows',
  canonical_property_chain: 'Salesforce parent account',
  contract_template_id: 'Contract template',
  le_contracts_geo: 'LE Contracts Geo',
  cost_currency: 'Cost currency',
  reporting_currency: 'Reporting currency',
  package_currencies: 'Package currencies',
};

const ACCOUNT_OR_PROPERTY_UNAVAILABLE = 'Required account or property evidence could not be loaded.';
const OPPORTUNITY_OR_ACCOUNT_UNAVAILABLE = 'Required opportunity or account evidence could not be loaded.';

export function getSetupSummary(row: CmChecklist.SetupRow) {
  const reasons = new Set(row.reason_codes);
  const unavailable = reasons.has('source_unavailable');
  const itemValue = (key: CmChecklist.SetupEvidenceKey) => row.evidence.items.find((item) => item.key === key)?.value;

  switch (row.rule_id) {
    case 'setup-account-name':
      if (unavailable) {
        return ACCOUNT_OR_PROPERTY_UNAVAILABLE;
      }
      return reasons.has('account_name_missing') || reasons.has('property_name_missing')
        ? 'Account or property name is missing.'
        : 'Account and property names were compared.';
    case 'setup-account-abn':
      if (unavailable) {
        return ACCOUNT_OR_PROPERTY_UNAVAILABLE;
      }
      if (reasons.has('property_country_missing')) {
        return 'Property country is missing.';
      }
      return itemValue('abn_required') === true
        ? 'Australian account ABN verification was checked.'
        : 'ABN verification is not required outside Australia.';
    case 'setup-account-currency':
      return unavailable
        ? 'Required account or opportunity evidence could not be loaded.'
        : 'Hotel account currency was compared with opportunity cost price currency.';
    case 'setup-campaign-schedules-exclusions':
      if (unavailable) {
        return 'Required opportunity evidence could not be loaded.';
      }
      if (reasons.has('exclusions_missing')) {
        return 'Brand and market exclusion evidence is not loaded.';
      }
      return itemValue('loaded_schedule_rows') === 0
        ? 'Campaign schedule rows are not loaded.'
        : 'Campaign schedules were checked against selected exclusions.';
    case 'setup-marketing-terms':
      return unavailable
        ? 'Required Salesforce account evidence could not be loaded.'
        : 'Salesforce parent account was compared with the matching contract template.';
    case 'setup-contracts-geo':
      return unavailable
        ? OPPORTUNITY_OR_ACCOUNT_UNAVAILABLE
        : 'LE Contracts Geo was compared with the matching contract template.';
    case 'setup-sell-cost-reporting-currency':
      return unavailable
        ? OPPORTUNITY_OR_ACCOUNT_UNAVAILABLE
        : 'Cost, reporting, and loaded package currencies were compared.';
    default:
      return '';
  }
}
